<!--
This component is responsible for displaying an individual form
based on provided configuration
-->
<template>
  <div class="form-display px-5 py-3 mb-5 bg-light rounded">
    <!-- Header start -->
    <div class="py-3" v-if="header">
      <h2>{{ header.title }}</h2>
      <p v-for="lead in header.items" :key="lead" class="header_items">
        {{ lead }}
      </p>
      <b
        ><p v-if="header.control_sum" style="text-align: right">
          Suma kontrolna: {{ this.count_control_sum(header.control_sum) }}
        </p></b
      >
      <hr class="col-xs-12" />
    </div>
    <!-- Header end -->
    <div class="row">
      <!-- Title page start -->
      <div v-if="title_page" class="col-lg-12 text-center title_page">
        <div class="py-2">
          <h2>{{ title_page.title }}</h2>
          <p
            v-for="lead in title_page.items"
            :key="lead"
            class="lead"
            align="justify"
          >
            {{ lead }}
          </p>
        </div>
        <b-img
          v-if="title_page.image_url"
          :src="title_page.image_url"
          fluid
          alt="Responsive image"
        ></b-img>
        <iframe
          v-if="title_page.video_id"
          width="560"
          height="315"
          :src="video_src(title_page.video_id)"
          frameborder="0"
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen
        ></iframe>
      </div>
      <!-- Form start -->
      <div v-if="form_schema" :class="setFormClass()">
        <form action method="post">
          <vue-form-generator
            :isNewModel="is_new_model"
            :model="model"
            :schema="form_schema"
            :options="options"
            tag="div"
            ref="generatedForm"
          ></vue-form-generator>
          <b-button
            variant="danger"
            class="float-left"
            v-if="add_multiple_visibility_remove_button"
            @click="clear_multiple_clicked"
            >Wyczyść</b-button
          >
          <b-button
            variant="success"
            class="float-right"
            v-if="add_multiple_visibility_add_button"
            :disabled="action_button_disabled(add_multiple)"
            @click="add_multiple_clicked"
            >Dodaj</b-button
          >
        </form>
      </div>
      <!-- Form end -->

      <!-- Help start -->
      <div v-if="help" class="col-lg-6 law-form-help">
        <div class="py-2">
          <h5>{{ help.title }}</h5>
          <p v-for="lead in help.items" :key="lead" class="lead">{{ lead }}</p>
        </div>
        <b-img
          v-if="help.image_url"
          :src="help.image_url"
          fluid
          alt="Responsive image"
        ></b-img>
        <iframe
          v-if="help.video_id"
          width="560"
          height="315"
          :src="video_src(help.video_id)"
          frameborder="0"
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen
        ></iframe>
      </div>
      <!-- Help end -->
      <!-- List start -->
      <div v-if="add_multiple_visible_if" :class="setAddMultipleClass()">
        <div class="py-2">
          <h5>{{ add_multiple.title }}</h5>
          <b-table
            :fields="this.add_multiple.names"
            :items="get_list_items(this.add_multiple.fields_visible_if)"
            thead-class="hidden_header"
            :striped="true"
            :borderless="true"
          >
            <template v-slot:cell(index)="data">{{ data.index + 1 }}</template>
            <template v-slot:cell(action)="row">
              <b-button
                :id="'remove_btn_' + row.index"
                @click="remove_multiple(row.index)"
                v-if="remove_button_visible(row.index)"
                variant="light"
                size="sm"
                class="text-right"
              >
                <b-icon icon="trash"></b-icon>
              </b-button>
            </template>
            <template v-slot:cell(required)="data">
              <span v-html="data.value"></span>
            </template>
            <template v-slot:cell(hint)="data">
              <div class="table_hint" v-if="data.value">
                <b-icon icon="question-square-fill"></b-icon>
                <span class="table_hint_text">{{ data.value }}</span>
              </div>
            </template>
            <template v-slot:cell(checkbox)="row">
              <b-form-checkbox
                :id="'checkbox_' + row.index"
                v-if="typeof row.item.checkbox != 'undefined'"
                v-model="row.item.checkbox"
              ></b-form-checkbox>
            </template>
          </b-table>
        </div>
      </div>
      <!-- List end -->
    </div>
    <!-- footer start -->
    <div v-if="footer">
      <hr class="col-xs-12" />
      <p v-for="lead in footer.items" :key="lead" class="footer_items">
        {{ lead }}
      </p>
    </div>
    <!-- footer end -->
    <!-- Actions start -->
    <hr class="col-xs-12" />
    <div class="row bottom_sticky">
      <div class="col">
        <button
          v-for="action in actions"
          :key="action.id"
          :id="action.id"
          type="button"
          :class="action_button_class(action)"
          @click="action_button_clicked(action)"
          :disabled="action_button_disabled(action)"
        >
          {{ action.label }}
          <b-spinner
            v-if="action.spinner"
            small
            id="download-spinner"
            style="display: none; margin-right: 0.75rem !important"
            class="float-left m-1"
          ></b-spinner>
        </button>
      </div>
    </div>
    <!-- Actions end -->
  </div>
</template>

<script>
import VueFormGenerator from "vue-form-generator";
import { Conditions } from "../steps/conditions.js";
import { getObjectValue } from "../utils/utils";
import { setObjectValue } from "../utils/utils";
import { pushObjectValue } from "../utils/utils";
import { removeObjectValue } from "../utils/utils";
import { getBankruptcyCourtsNames } from "../courts/BankruptcyCourts";
import { FillInTheField } from "../steps/fill_field.js";

export default {
  name: "FormDisplay",
  components: {
    "vue-form-generator": VueFormGenerator.component
  },
  computed: {
    add_multiple_visible_if: function() {
      if (this.add_multiple) {
        var conditions = new Conditions(
          this.add_multiple.visible_if,
          this.model
        );
        return conditions.is_all_valid;
      } else {
        return false;
      }
    },
    add_multiple_visibility_remove_button: function() {
      if (this.add_multiple) {
        return this.add_multiple.buttons;
      } else {
        return false;
      }
    },
    add_multiple_visibility_add_button: function() {
      if (this.add_multiple) {
        if (this.add_multiple.add_visible_if) {
          var conditions = new Conditions(
            this.add_multiple.add_visible_if,
            this.model
          );
          if (!conditions.is_all_valid) return false;
        }
        return this.add_multiple.buttons;
      } else {
        return false;
      }
    }
  },
  data() {
    return {
      is_form_valid: false,
      is_new_model: true,
      custom_tags: [],
      autocomplete_filter: null,
      options: {
        validateAfterLoad: true,
        validateAfterChanged: true,
        validateAsync: false
      }
    };
  },
  methods: {
    count_control_sum: function(fields) {
      var $this = this;
      var sum = 0.0;

      fields.forEach(function(element) {
        var value = getObjectValue($this.model, element);
        if (Array.isArray(value)) {
          value.forEach(function(element) {
            if (element["value"]) sum += parseFloat(element["value"]);
          });
        } else if (value) sum += parseFloat(value);
      });
      return this.number_field(sum) + "\xa0zł";
    },
    other_name: function(value, name, config) {
      if (config["other_name"]) {
        return config["other_" + name];
      } else {
        return value;
      }
    },
    // return youtube video url that is used in iframe
    video_src: function(video_id) {
      return `https://www.youtube.com/embed/${video_id}`;
    },
    // set format for cell in table to number with currency
    number_field(value) {
      if (typeof value == "string") {
        return `${Number(
          parseFloat(value.replace(",", ".")).toFixed(2)
        ).toLocaleString()}`;
      } else {
        return `${Number(parseFloat(value).toFixed(2)).toLocaleString()}`;
      }
    },
    currency_field(value) {
      return `${this.number_field(value)}\xa0${this.add_multiple.currency}`;
    },
    required_field(value) {
      if (value) {
        return "*".fontcolor("red");
      }
    },
    setFormClass: function() {
      if (this.help || this.add_multiple) {
        return "col-lg-6";
      } else {
        return this.schema_classes;
      }
    },
    setAddMultipleClass: function() {
      if (this.add_multiple.add_multiple_classes) {
        return this.add_multiple.add_multiple_classes;
      } else {
        return "col-lg-6 multiple-list";
      }
    },
    // return css class for the action button
    action_button_class: function(action) {
      return `btn btn-${action.button_style} float-${action.align} mx-2`;
    },
    // run when action button is clicked
    action_button_clicked: function(action) {
      var $this = this;

      action.actions.forEach(function(action_object) {
        // check if action object meet conditions

        var conditions = new Conditions(action_object.conditions, $this.model);
        if (conditions.is_all_valid == false) {
          console.log("Button clicked but action is not valid", action_object);
          return;
        }
        // addition functionality of fill_data in action_button
        if (action_object.fill_data) {
          new FillInTheField(action_object, $this.model);
        }
        if (action_object.name == "next_step") {
          console.log("ACTION OBJECT:", action_object.step_id);
          $this.action_save(action_object.step_id);
          $this.action_next_step(action_object.step_id);
        } else if (action_object.name == "download_button") {
          $this.action_generate_form();
        } else if (action_object.name == "previous_step") {
          $this.action_save(action_object.step_id);
          $this.action_previous_step(action_object.step_id);
        } else if (action_object.name == "user_register") {
          $this.action_user_register(action_object);
        } else if (action_object.name == "change_route") {
          $this.action_change_route(action_object.route_name);
        }
      });
    },
    // return True if provided action button should be disabled
    // False if not
    // It supports few key possibilities:
    // - disable when form is not valid
    // - disable based on model parameter values
    // - disable based on provided boolean value in a config
    action_button_disabled: function(action) {
      if (action.disabled == "invalid_form") {
        return this.is_form_valid == false;
      } else if (action.disabled == "invalid_list") {
        return this.get_list_items() == false;
      } else {
        return action.disabled;
      }
    },
    // return list of items from add_multiple.field (add_multiple)
    get_list_items: function(field_name) {
      var all_items = getObjectValue(this.model, this.add_multiple.field);

      if (field_name) {
        return all_items.filter(el => el[field_name]);
      }
      return all_items;
    },
    // collecting data from frontend and pushing them to the list in model
    add_multiple_clicked: function() {
      var multiple_object = {};
      var $this = this;
      this.add_multiple.values.forEach(function(element) {
        if (String(element.value).includes(".")) {
          multiple_object[element.key] = getObjectValue(
            $this.model,
            element.value
          );
        } else {
          multiple_object[element.key] = element.value;
        }
      });
      pushObjectValue($this.model, multiple_object, this.add_multiple.field);
      this.clear_multiple_clicked();
      this.action_save(this.current_step_id);
    },
    // deleting object from list (add_multiple)
    remove_multiple: function(index) {
      removeObjectValue(this.model, this.add_multiple.field, index);
      this.action_save(this.current_step_id);
    },
    // sets (remove button) in table to visible if row is in status required
    remove_button_visible: function(index) {
      var value = getObjectValue(this.model, this.add_multiple.field);
      if (value[index]["required"] === true) {
        return false;
      } else {
        return true;
      }
    },
    // clearing filled fields in form (add_multiple)
    clear_multiple_clicked: function() {
      var $this = this;
      this.add_multiple.values.forEach(function(element) {
        if (String(element.value).includes(".")) {
          // clear when element.value is path to model
          setObjectValue($this.model, null, element.value);
        }
      });
    },
    // Run when button with change_route action is clicked
    action_change_route: function(route_name) {
      this.$emit("action_change_route", route_name);
    },
    // Run when button with next step action is clicked
    action_next_step: function(step_id) {
      this.$emit("action_next_step", step_id);
    },
    // Run when button with action previous step is clicked
    action_previous_step: function(step_id) {
      this.$emit("action_previous_step", step_id);
    },
    // Run when button with save action is clicked
    action_save: function(step_id) {
      this.$emit("action_save", step_id);
    },
    // Run when button with download action is clicked
    action_generate_form: function(step_id) {
      // disabling button and adding spinner
      const button = document.getElementById("download-button");
      button.disabled = true;
      const download_spinner = document.getElementById("download-spinner");
      download_spinner.style.display = "block";

      this.$emit("action_generate_form", step_id);
    },
    // Run when button with user register action is clicked
    action_user_register: function(user_register_config) {
      this.$emit("action_user_register", user_register_config);
    },
    autocomplete_on_open: function() {
      this.autocomplete_send_query("");
    },
    // sending query to lambda to get response from database
    autocomplete_send_query: function(searchQuery) {
      if (this.autocomplete_filter) {
        if (this.autocomplete_filter.includes(".")) {
          var filter = getObjectValue(this.model, this.autocomplete_filter);
        } else {
          filter = this.autocomplete_filter;
        }

        this.$emit("action_autocomplete", searchQuery, filter.toLowerCase());
      } else {
        this.$emit("action_autocomplete", searchQuery);
      }
    },
    // return concatted values of autocomplete
    autocomplete_merge_values: function() {
      return this.autocomplete_list_name.concat(this.custom_tags);
    },
    // adds new tag to autocomplete
    autocomplete_add_new_tag: function(newTag) {
      this.custom_tags.push(newTag);
    },
    show_modal: function(_, field) {
      var modal_options = field.buttons.find(x => x.name === "show_modal");
      const h = this.$createElement;
      // Using HTML string
      const titleVNode = h("div", {
        domProps: { innerHTML: modal_options.title }
      });
      // More complex structure
      const content = h("div", { class: ["content"] }, [
        h("p", { class: ["justify"] }, [modal_options.content])
      ]);
      // We must pass the generated VNodes as arrays
      this.$bvModal.msgBoxOk([content], {
        title: [titleVNode],
        buttonSize: "md",
        centered: true
      });
    },
    edit_reason: function(model, field) {
      // have to find button again couse i cant pass this parameters to function
      var edit_reason_btn = field.buttons.find(x => x.name === "edit_reason");
      var value = getObjectValue(model, edit_reason_btn.field);
      if (value == "Tekst wygenerowany") {
        edit_reason_btn.label = "Przywróć";
        setObjectValue(model, "Tekst edytowany", edit_reason_btn.field);
      } else {
        edit_reason_btn.label = "Edytuj";
        setObjectValue(model, "Tekst wygenerowany", edit_reason_btn.field);
      }
    },
    // Run every time model is updated
    handle_model_update: function() {
      // check if form is valid
      var initial_form_valid = this.$refs.generatedForm.errors.length == 0;
      this.$refs.generatedForm.validate();
      this.is_form_valid = this.$refs.generatedForm.errors.length == 0;
      if (initial_form_valid) {
        this.$refs.generatedForm.clearValidationErrors();
      }
    }
  },
  updated() {
    var fields = this.$refs.generatedForm.$children;
    var $this = this;
    fields.forEach(element => {
      if (element.field.buttons) {
        var modal_button = element.field.buttons.find(
          x => x.name === "show_modal"
        );
        var edit_reason_btn = element.field.buttons.find(
          x => x.name === "edit_reason"
        );
        if (typeof modal_button != "undefined") {
          modal_button.onclick = this.show_modal;
        }
        if (typeof edit_reason_btn != "undefined") {
          var value = getObjectValue($this.model, edit_reason_btn.field);
          // changing label if it needed to on step change
          if (value == "Tekst wygenerowany") {
            edit_reason_btn.label = "Edytuj";
          } else {
            edit_reason_btn.label = "Przywróć";
          }
          edit_reason_btn.onclick = this.edit_reason;
        }
      }
    });
  },
  watch: {
    model: {
      deep: true,
      handler() {
        this.$nextTick(function() {
          this.handle_model_update();
        });
      }
    },
    form_schema: {
      deep: true,
      handler() {
        this.$nextTick(function() {
          this.handle_model_update();
          var fields = this.$refs.generatedForm.schema.groups[0].fields;
          fields.forEach(element => {
            if (element.fill_data) {
              element.fill_data.forEach(parameter => {
                if (parameter.name == "fill_court_list") {
                  element.values = getBankruptcyCourtsNames;
                }
              });
            }
            if (element.is_autocomplete) {
              element.selectOptions.onSearch = this.autocomplete_send_query;
              element.values = this.autocomplete_merge_values;
              element.selectOptions.onNewTag = this.autocomplete_add_new_tag;
              element.selectOptions.onOpen = this.autocomplete_on_open;
              this.autocomplete_filter = element.autocomplete_filter;
            }
          });
          window.scrollTo(0, 0);
        });
      }
    }
  },
  props: {
    current_step_id: String,
    schema_classes: String,
    actions: Array,
    add_multiple: Object,
    form_schema: Object,
    header: Object,
    help: Object,
    autocomplete_list_name: Array,
    title_page: Object,
    model: Object,
    footer: Object
  }
};
</script>

<style>
iframe {
  max-width: 100%;
}
.hidden_header {
  display: none;
}
.form-group.field-checkbox {
  display: flex;
  flex-wrap: wrap-reverse;
  flex-direction: row-reverse;
  justify-content: flex-end;
}

.vue-form-generator .form-group.field-checkbox input {
  margin-left: 0px;
  margin-right: 10px;
  margin-top: 4px;
}
.form-display table .btn-sm {
  padding: 0rem 0.5rem !important;
}
.form-display table .btn-light {
  background-color: transparent !important;
  border-color: transparent !important;
}
.align_right {
  text-align: right;
}
.buttons {
  margin-left: auto !important;
  margin-bottom: auto !important;
}
.lead {
  font-size: 1rem !important;
}
.btn_modal {
  color: #ffffff !important;
  background-color: #5bc0de !important;
  border-color: #5bc0de !important;
}
.title_page {
  margin-top: 10px;
  margin-bottom: 20px;
}
textarea {
  min-height: 125px;
}
.header_items {
  margin-bottom: 0 !important;
}
.footer_items {
  font-size: 0.8em !important;
}
.justify {
  text-align: justify;
}
.table_hint {
  position: relative;
  display: inline-block;
}

.table_hint .table_hint_text {
  visibility: hidden;
  width: 200px;
  background-color: black;
  color: #fff;
  text-align: left;
  border-radius: 6px;
  padding: 5px 0 0 5px;

  /* Position the table_hint */
  position: absolute;
  z-index: 1;
  top: 100%;
  left: 50%;
  margin-left: -60px;
}

.table_hint:hover .table_hint_text {
  visibility: visible;
}

.reason_label {
  color: red;
}
</style>
